import React, { useEffect, useRef, useState } from 'react';

import * as d3 from 'd3';

import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import { getPieChartColor } from './colors';

const PieChart = ({ label, data, type, legendPosition = 'bottom' }) => {
  const svgRef = useRef();
  const chartRef = useRef();
  const [activeTooltip, setActiveTooltip] = useState(null)
  
  const getPercentage = (value, total) => total ? `${((value / total) * 100).toFixed(1)}%` : '0.0%'

  const getTooltipText = (row, total) => {
    if (row.tooltip)
      return row.tooltip
    return `${row.label}: ${row.value} (${getPercentage(row.value, total)})`
  }

  const getChartTooltipText = (row, total, type) => {
    if (type === 'percentage')
      return `${row.label}: ${getPercentage(row.value, total)}`
    return `${row.label}: ${row.value}`
  }

  const [total, setTotal] = useState(0)

  useEffect(() => {
    let counter = 0
    data.forEach((row) => {
      counter += row.value
    })
    setTotal(counter)
  }, [data])

  useEffect(() => {

    setActiveTooltip(null)

    if (data.length === 0)
      return

    const svg = d3.select(svgRef.current);

    svg.selectAll('*').remove();

    const width = parseInt(svg.style('width'));
    const height = parseInt(svg.style('height'));
    const radius = Math.min(width, height) / 2;

    svg.attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const mainGroup = svg.append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    const pie = d3.pie().value(d => d.value);
    const arc = d3.arc().innerRadius(0).outerRadius(radius);

    const color = d3.scaleOrdinal(d3.schemeCategory10);
    const currentTotal = data.reduce((sum, row) => sum + row.value, 0)
    const pieData = pie(data);
    const showTooltip = (event, d) => {
      const [x, y] = d3.pointer(event, chartRef.current)
      setActiveTooltip({
        text: getChartTooltipText(d.data, currentTotal, type),
        x: x + 12,
        y: y + 12
      })
    }

    mainGroup.selectAll('path')
      .data(pieData)
      .enter()
      .append('path')
      .attr('d', arc)
      .attr('fill', (d, i) => getPieChartColor(d.data.label) || color(i))
      .attr('stroke', 'white')
      .attr('stroke-width', 1)
      .style('cursor', 'default')
      .on('mouseenter', function(event, d) {
        d3.select(this).attr('stroke-width', 2)
        showTooltip(event, d)
      })
      .on('mousemove', showTooltip)
      .on('mouseleave', function() {
        d3.select(this).attr('stroke-width', 1)
        setActiveTooltip(null)
      });
  }, [data, type]);

  return (data.length !== 0 && 
    (
      <div style={{ display: 'flex', flexDirection: (legendPosition === 'bottom') ? 'column' : 'row' }}>
        <div ref={chartRef} style={{ width: '100%', height: '100%', padding: '2rem', position: 'relative' }}>
          <Typography variant="subtitle1">{label}</Typography>
          <svg ref={svgRef} style={{ width: '100%', height: '100%' }}></svg>
          {activeTooltip &&
            <div style={{
              position: 'absolute',
              left: activeTooltip.x,
              top: activeTooltip.y,
              zIndex: 2,
              maxWidth: '16rem',
              padding: '.4rem .55rem',
              borderRadius: '4px',
              backgroundColor: 'rgba(33, 68, 108, .94)',
              color: '#ffffff',
              fontSize: '.75rem',
              lineHeight: 1.25,
              pointerEvents: 'none',
              boxShadow: '0 2px 8px rgba(0, 0, 0, .2)'
            }}>
              {activeTooltip.text}
            </div>
          }
        </div>
        <div className='responsive-legend' style={{ display: 'flex', flexDirection: (legendPosition === 'bottom') ? 'row' : 'column', justifyContent: 'center', marginTop: (legendPosition === 'bottom') ? '.5rem' : '3rem' }}>
          {data.map((d, i) => (
            <Tooltip key={i} title={getTooltipText(d, total)} placement="top" disableInteractive>
              <div style={{ display: 'flex', alignItems: (legendPosition === 'bottom') ? 'center' : 'flex-start', margin: '0 .25rem', cursor: 'default' }}>
                {d.icon &&
                  <>
                    <div className={`graph-overlay ${d.label}`} style={{ margin: '0 .25rem 0 .25rem', fill: getPieChartColor(d.label) }}>{d.icon}</div>
                    <span style={{ whiteSpace: 'nowrap', fontSize: "90%" }}>{type === "number" ? d.value : getPercentage(d.value, total)}</span>
                  </>
                }
                {! d.icon &&
                  <>
                    <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: getPieChartColor(d.label) || d3.schemeCategory10[i],
                      margin: (legendPosition === 'bottom') ? '0 .5rem 0 0' : '0 .5rem 1rem 0',
                    }}></div>
                    <span style={{ whiteSpace: 'nowrap', fontSize: "90%" }}>{type === "number" ? `${d.label} (${d.value})` : `${d.label} (${getPercentage(d.value, total)})`}</span>
                  </>
                }
              </div>
            </Tooltip>
          ))}
        </div>
      </div>
    )
  );
};

export default PieChart;
